import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  BUSINESS_PROFILES,
  disableModule,
  enableModule,
  fetchModuleStats,
  listModules,
  MODULE_CATEGORIES,
  moduleCatalogMeta,
  recommendedModulesFor,
  type CoreModule,
} from '@/lib/coreModules';
import {
  deleteExtension,
  extensionKindMeta,
  EXTENSION_KINDS,
  fetchExtensibilitySummary,
  listExtensions,
  registerExtension,
  SAMPLE_MODULE_EXTENSIONS,
  type CoreExtension,
  type ExtensionKind,
} from '@/lib/coreExtensibility';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ModulesTab({ org }: Props) {
  const [modules, setModules] = useState<CoreModule[]>([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, disabled: 0, byCategory: {} as Record<string, number> });
  const [extensions, setExtensions] = useState<CoreExtension[]>([]);
  const [extSummary, setExtSummary] = useState({ total: 0, byKind: {} as Record<string, number>, byModule: [] as string[] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [profile, setProfile] = useState<string>('freelance');
  const [selected, setSelected] = useState<CoreModule | null>(null);
  const [extForm, setExtForm] = useState<{ moduleKey: string; kind: ExtensionKind; key: string; label: string }>({ moduleKey: 'loyalty', kind: 'entity', key: '', label: '' });
  const [extBusy, setExtBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [m, s, e, es] = await Promise.all([
        listModules(org.id),
        fetchModuleStats(org.id),
        listExtensions(org.id),
        fetchExtensibilitySummary(org.id),
      ]);
      setModules(m);
      setStats(s);
      setExtensions(e);
      setExtSummary(es);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (m: CoreModule) => {
    const enabled = m.is_core || m.status === 'enabled';
    if (m.is_core) return;
    setBusy(m.module_key);
    try {
      if (enabled) await disableModule(org.id, m.module_key);
      else await enableModule({ tenantId: org.tenant_id, organizationId: org.id, moduleKey: m.module_key, version: m.version || '1.0' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setBusy(null);
    }
  };

  const registerSample = async () => {
    setExtBusy(true);
    setError('');
    try {
      for (const ext of SAMPLE_MODULE_EXTENSIONS) {
        await registerExtension({ tenantId: org.tenant_id, organizationId: org.id, ...ext });
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setExtBusy(false);
    }
  };

  const registerOne = async () => {
    if (!extForm.key.trim()) return;
    setExtBusy(true);
    try {
      await registerExtension({ tenantId: org.tenant_id, organizationId: org.id, moduleKey: extForm.moduleKey, kind: extForm.kind, key: extForm.key.trim(), label: extForm.label.trim() || extForm.key.trim() });
      setExtForm({ ...extForm, key: '', label: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setExtBusy(false);
    }
  };

  const grouped = MODULE_CATEGORIES.map((c) => ({ ...c, items: modules.filter((m) => (m.category || 'Autre') === c.key) })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Modularité & registre</span> : Zifek Core fonctionne même sans aucun module avancé.
        Chaque module est enregistré (status, version, permissions, tools, entities, events). Un futur module peut
        <span className="font-medium text-foreground-600"> s’enregistrer sans modifier le Core</span>.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-apps-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Modules au registre</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.enabled}</p>
          <p className="text-xs text-foreground-500">Activés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-puzzle-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{extSummary.total}</p>
          <p className="text-xs text-foreground-500">Extensions enregistrées</p>
        </div>
      </div>

      {/* Profils d'activité (modularité) */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-layout-grid-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Combinaisons recommandées par activité</h4>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {BUSINESS_PROFILES.map((p) => (
              <button key={p.key} onClick={() => setProfile(p.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${profile === p.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'}`}>
                <i className={p.icon}></i>{p.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-foreground-400 mb-2">{BUSINESS_PROFILES.find((p) => p.key === profile)?.desc}</p>
          <div className="flex flex-wrap gap-1.5">
            {recommendedModulesFor(profile).map((m) => (
              <span key={m.key} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium flex items-center gap-1"><i className={m.icon}></i>{m.name.replace('Zifek ', '')}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Registre des modules */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.key} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b border-background-200/60 flex items-center gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{g.label}</h4>
              </div>
              <div className="divide-y divide-background-200/60">
                {g.items.map((m) => {
                  const cat = moduleCatalogMeta(m.module_key);
                  const enabled = m.is_core || m.status === 'enabled';
                  return (
                    <div key={m.module_key} className="flex items-center gap-3 px-4 py-3">
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}><i className={cat.icon}></i></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-800 truncate flex items-center gap-2">
                          {m.name || cat.name}
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">v{m.version || '1.0'}</span>
                          {m.is_core && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900">core</span>}
                        </p>
                        <p className="text-[11px] text-foreground-400 truncate">
                          <code className="bg-background-100 px-1 rounded">{m.module_key}</code> · {(m.permissions || []).length} perm. · {(m.tools || []).length} tools · {(m.entities || []).length} entités
                        </p>
                      </div>
                      <button onClick={() => setSelected(m)} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">Détails</button>
                      {busy === m.module_key && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                      <button
                        onClick={() => toggle(m)}
                        disabled={m.is_core || busy === m.module_key}
                        title={m.is_core ? 'Le Core est toujours actif' : ''}
                        className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-primary-500' : 'bg-background-200'} ${m.is_core ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${enabled ? 'left-[26px]' : 'left-0.5'}`}></span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Détail module */}
      {selected && (
        <div className="bg-background-50 border border-primary-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className={moduleCatalogMeta(selected.module_key).icon}></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">{selected.name} — registre du module</h4>
            <button onClick={() => setSelected(null)} className="ml-auto w-7 h-7 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-sm"></i></button>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {[
              { label: 'Statut', value: selected.status || '—', icon: 'ri-toggle-line' },
              { label: 'Version', value: selected.version || '1.0', icon: 'ri-git-branch-line' },
              { label: 'Permissions', value: (selected.permissions || []).join(', ') || '—', icon: 'ri-shield-keyhole-line' },
              { label: 'Outils AI', value: (selected.tools || []).join(', ') || '—', icon: 'ri-robot-2-line' },
              { label: 'Entités', value: (selected.entities || []).join(', ') || '—', icon: 'ri-database-2-line' },
              { label: 'Événements', value: (selected.events || []).join(', ') || '—', icon: 'ri-flashlight-line' },
              { label: 'Pages', value: (selected.pages || []).join(', ') || '—', icon: 'ri-layout-2-line' },
              { label: 'Workflows', value: (selected.workflows || []).join(', ') || '—', icon: 'ri-flow-chart' },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-2 bg-background-100/60 rounded-md p-2.5">
                <i className={`${row.icon} text-foreground-500 mt-0.5`}></i>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground-700">{row.label}</p>
                  <p className="text-foreground-500 break-words">{row.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extensibilité */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-puzzle-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Extensibilité — enregistrer sans toucher au Core</h4>
          <button onClick={registerSample} disabled={extBusy} className="ml-auto px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
            {extBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>} Enregistrer un module « Loyalty »
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {EXTENSION_KINDS.map((k) => (
              <span key={k.key} className={`px-2 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1 ${k.className}`}>
                <i className={k.icon}></i>{k.label} <span className="opacity-70">{extSummary.byKind[k.key] || 0}</span>
              </span>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input className={INPUT} placeholder="Module (ex : loyalty)" value={extForm.moduleKey} onChange={(e) => setExtForm({ ...extForm, moduleKey: e.target.value })} />
            <select className={INPUT} value={extForm.kind} onChange={(e) => setExtForm({ ...extForm, kind: e.target.value as ExtensionKind })}>
              {EXTENSION_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
            <input className={INPUT} placeholder="Clé (ex : loyalty_card)" value={extForm.key} onChange={(e) => setExtForm({ ...extForm, key: e.target.value })} />
            <button onClick={registerOne} disabled={extBusy || !extForm.key.trim()} className="px-3 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-50">
              Enregistrer
            </button>
          </div>

          {extensions.length === 0 ? (
            <p className="text-xs text-foreground-400 text-center py-3">Aucune extension enregistrée. Essayez le module « Loyalty » ci-dessus.</p>
          ) : (
            <div className="divide-y divide-background-200/60">
              {extensions.map((ext) => {
                const k = extensionKindMeta(ext.kind);
                return (
                  <div key={ext.id} className="flex items-center gap-3 py-2">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${k.className}`}><i className={k.icon}></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground-800 truncate">{ext.label || ext.key} <span className="text-foreground-400">· {ext.module_key}</span></p>
                      <p className="text-[11px] text-foreground-400 truncate">{k.label} · <code className="bg-background-100 px-1 rounded">{ext.key}</code></p>
                    </div>
                    <button onClick={() => deleteExtension(ext.id).then(load)} className="w-7 h-7 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-600 transition-colors">
                      <i className="ri-delete-bin-6-line text-xs"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}